import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Loader from '../../global/loader';
import apiRequest from '../../utils/apiRequest';
import CompletedStatus from '../../components/StatusButton/CompletedStatus';
import PendingStatus from '../../components/StatusButton/PendingStatus';
import formatDate from '../../utils/formatDate';

type CustomerType = {
  email: string;
  id: string;
  name: string;
  shopifyId: string;
  tierId: string;
  currentTier: TierData;
};

type TierData = {
  name: String;
};

type OrderItem = {
  orderId: string;
  createdAt: string;
  paymentStatus: string;
  total: string;
};

type OrderResponse = {
  items: OrderItem[];
  hasNextPage: boolean;
  endCursor: string;
};

type ActivityResponse = {
  orders: ActivityItems[];
  hasNextPage: boolean;
  endCursor: string;
};

type ActivityItems = {
  date: string;
  spendOrRefund: string;
  name: string;
};

const CustomerDetails = () => {
  const [loader, setLoader] = useState(true);
  const [customerData, setCustomerData] = useState<CustomerType>();
  const [customerOrders, setCustomerOrders] = useState<OrderResponse>();
  const [customerActivity, setCustomerActivity] = useState<ActivityResponse>();
  const [pageCursors, setPageCursors] = useState<string[]>([]);
  const [activityPageCursors, setActivityPageCursors] = useState<string[]>([]);
  const [currentActivityPage, setCurrentActivityPage] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const customerId = queryParams.get('id');

  const getCustomerData = async () => {
    setLoader(true);

    const params = {
      shopifyId: customerId || '',
    };

    const res = await apiRequest('getCustomerById', null, params);

    if (res.error) {
      setCustomerData(undefined);
    } else {
      setCustomerData(res.data);
    }

    setLoader(false);
  };

  const getCustomerOrders = async (cursor?: string) => {
    setLoader(true);

    const params = {
      shopifyId: customerId || '',
      ...(cursor && { cursor }),
    };

    const res = await apiRequest('customerOrderList', null, params);

    if (res.error) {
      setCustomerOrders(undefined);
    } else {
      setCustomerOrders(res.data);

      if (cursor) {
        setPageCursors((prev) => [...prev, cursor]);
      }
    }

    setLoader(false);
  };

  const getCustomerActivity = async (cursor?: string) => {
    setLoader(true);

    const params = {
      shopifyId: customerId || '',
      ...(cursor && { cursor }),
    };

    const res = await apiRequest('getCustomerActivity', null, params);

    if (res.error) {
      setCustomerActivity(undefined);
    } else {
      setCustomerActivity(res.data);

      if (cursor) {
        setActivityPageCursors((prev) => [...prev, cursor]);
      }
    }

    setLoader(false);
  };

  const handleNextPage = () => {
    if (customerOrders?.hasNextPage) {
      setCurrentPage((prev) => prev + 1);
      getCustomerOrders(customerOrders.endCursor);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
      const previousCursor = pageCursors[currentPage - 1];
      getCustomerOrders(previousCursor);
    }
  };

  const handleActivityNextPage = () => {
    if (customerActivity?.hasNextPage) {
      setCurrentActivityPage((prev) => prev + 1);
      getCustomerActivity(customerActivity.endCursor);
    }
  };

  const handleActivityPreviousPage = () => {
    if (currentActivityPage > 0) {
      setCurrentActivityPage((prev) => prev - 1);
      const previousCursor = activityPageCursors[currentPage - 1];
      getCustomerActivity(previousCursor);
    }
  };

  useEffect(() => {
    if (customerId) {
      getCustomerData();
      getCustomerOrders();
      getCustomerActivity();
    } else {
      navigate(-1);
    }
  }, [customerId]);

  return (
    <>
      {loader && <Loader showHide={loader} />}
      {!loader && (
        <div className='overflow-x-auto p-8'>
          <div className='grid grid-cols-2 items-center pb-6'>
            <div className='justify-self-start'>
              <h2 className='text-xl font-bold mb-2'>
                {customerData && customerData.name}
              </h2>
            </div>
          </div>
          <div className='grid grid-flow-col gap-3'>
            <div className='col-span-4'>
              <div className='bg-white mb-3'>
                <div className='p-4'>
                  <div className='grid grid-flow-col gap-3'>
                    <div className='col-span-6'>
                      <h2 className='text-xl font-bold mb-2'>Activity</h2>
                    </div>
                    <div className='col-span-1 justify-self-end'>
                      <div className='inline-flex'>
                        <button
                          onClick={handleActivityPreviousPage}
                          disabled={currentActivityPage === 0}
                          className='w-[77px] text-[12px] h-[30px] border rounded-[3px] mr-[2px] hover:bg-[#f2f0f0] cursor-pointer disabled:bg-[#f2f0f0] disabled:text-[#9e9595]'
                        >
                          Previous
                        </button>
                        <button
                          onClick={handleActivityNextPage}
                          disabled={!customerOrders?.hasNextPage}
                          className='w-[77px] text-[12px] h-[30px] border rounded-[3px] mr-[2px] hover:bg-[#f2f0f0] cursor-pointer disabled:bg-[#f2f0f0] disabled:text-[#9e9595]'
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>

                  <table className='min-w-full'>
                    <thead className='bg-gray-200'>
                      <tr className='text-left bg-white'>
                        <th className='py-2 px-4 border-b border-[#E6E6E6] text-[#656565] text-[17px] font-[500]'>
                          Action
                        </th>
                        <th className='py-2 px-4 border-b border-[#E6E6E6] text-[#656565] text-[17px] font-[500]'>
                          Paid / Refund
                        </th>
                        <th className='py-2 px-4 border-b border-[#E6E6E6] text-[#656565] text-[17px] font-[500]'>
                          Date
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerActivity &&
                        customerActivity.orders.map((item, index) => (
                          <tr key={index} className='text-left'>
                            <td className=' py-[13px] px-4 border-b text-[#656565] border-[#E6E6E6] font-normal text-[16px]'>
                              {item.name}
                            </td>
                            <td className='py-2 px-4 border-b text-[#656565] border-[#E6E6E6] font-normal text-[16px]'>
                              ₹ {item.spendOrRefund}
                            </td>

                            <td className='py-2 px-4 border-b text-[#656565] border-[#E6E6E6] font-normal text-[16px]'>
                              {item?.date ? formatDate(item?.date) : ''}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className='bg-white mb-3'>
                <div className='p-4'>
                  <div className='grid grid-flow-col gap-3'>
                    <div className='col-span-6'>
                      <h2 className='text-xl font-bold mb-2'>Orders</h2>
                    </div>
                    <div className='col-span-1 justify-self-end'>
                      <div className='inline-flex'>
                        <button
                          onClick={handlePreviousPage}
                          disabled={currentPage === 0}
                          className='w-[77px] text-[12px] h-[30px] border rounded-[3px] mr-[2px] hover:bg-[#f2f0f0] cursor-pointer disabled:bg-[#f2f0f0] disabled:text-[#9e9595]'
                        >
                          Previous
                        </button>
                        <button
                          onClick={handleNextPage}
                          disabled={!customerOrders?.hasNextPage}
                          className='w-[77px] text-[12px] h-[30px] border rounded-[3px] mr-[2px] hover:bg-[#f2f0f0] cursor-pointer disabled:bg-[#f2f0f0] disabled:text-[#9e9595]'
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>

                  <table className='min-w-full'>
                    <thead className='bg-gray-200'>
                      <tr className='text-left bg-white'>
                        <th className='py-2 px-4 border-b border-[#E6E6E6] text-[#656565] text-[17px]  font-[500]'>
                          Order
                        </th>
                        <th className='py-2 px-4 font-[500] border-b text-[#656565] text-[17px] border-[#E6E6E6] '>
                          Total
                        </th>
                        <th className='py-2 px-4 font-[500] border-b text-[#656565] text-[17px] border-[#E6E6E6] '>
                          Payment
                        </th>
                        <th className='py-2 px-4 font-[500] border-b text-[#656565] text-[17px] border-[#E6E6E6] '>
                          Date
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerOrders &&
                        customerOrders.items.map((item, index) => (
                          <tr key={index} className='text-left'>
                            <td className='py-[13px] px-4 border-b'>
                              <Link
                                to={''}
                                className='text-blue-600 underline text-[16px]'
                              >
                                {item.orderId}
                              </Link>
                            </td>
                            <td className='py-2 px-4 border-b text-[#656565] border-[#E6E6E6] font-normal text-[16px]'>
                              ₹ {item.total}
                            </td>
                            <td className='py-2 px-4 border-b text-[#656565] border-[#E6E6E6] font-normal text-[16px]'>
                              {item.paymentStatus === 'PAID' ? (
                                <CompletedStatus />
                              ) : (
                                <PendingStatus />
                              )}
                            </td>
                            <td className='py-2 px-4 border-b text-[#656565] border-[#E6E6E6] font-normal text-[16px]'>
                              {item?.createdAt
                                ? formatDate(item?.createdAt)
                                : ''}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className='col-span-1'>
              <div className='bg-white pb-3 mb-3'>
                <div className='p-4'>
                  <h2 className='text-xl font-bold mb-2'>Details</h2>
                  <p className='text-blue-600'>
                    {customerData && customerData.email}
                  </p>

                  <p className='text-gray-800 font-semibold'>
                    Tier :{' '}
                    {customerData?.currentTier &&
                      customerData?.currentTier?.name}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerDetails;
